<template>
  <div class="user-create">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="title-section">
          <h1 class="page-title">
            <i class="bi bi-plus-circle me-2"></i>
            Crear Usuario
          </h1>
          <p class="page-description">
            Complete el formulario para crear un nuevo usuario
          </p>
        </div>
        <div class="actions">
          <router-link to="/users" class="btn btn-outline-secondary">
            <i class="bi bi-arrow-left me-2"></i>
            Volver
          </router-link>
        </div>
      </div>
    </div>

    <!-- Form Card -->
    <div class="card">
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <!-- Basic Information -->
          <div class="form-section mb-4">
            <h5 class="form-section-title">
              <i class="bi bi-info-circle me-2"></i>
              Información Básica
            </h5>

            <div class="row g-3">
              <div class="col-md-6">
                <label for="fullname" class="form-label">
                  Nombre Completo
                </label>
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.fullname }"
                  id="fullname"
                  v-model="formData.fullname"
                  placeholder="Ej: Juan Pérez"
                >
                <div class="invalid-feedback" v-if="errors.fullname">
                  {{ errors.fullname }}
                </div>
                <small class="form-text text-muted">
                  Nombre completo del usuario (opcional)
                </small>
              </div>

              <div class="col-md-6">
                <label for="email" class="form-label">
                  Email <span class="text-danger">*</span>
                </label>
                <input
                  type="email"
                  class="form-control"
                  :class="{ 'is-invalid': errors.email }"
                  id="email"
                  v-model="formData.email"
                  placeholder="usuario@ejemplo.com"
                  required
                >
                <div class="invalid-feedback" v-if="errors.email">
                  {{ errors.email }}
                </div>
                <small class="form-text text-muted">
                  Email único del usuario para autenticación
                </small>
              </div>

              <div class="col-md-6">
                <label for="password" class="form-label">
                  Contraseña <span class="text-danger">*</span>
                </label>
                <div class="input-group">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': errors.password }"
                    id="password"
                    v-model="formData.password"
                    placeholder="••••••••"
                    required
                    minlength="8"
                  >
                  <button
                    class="btn btn-outline-secondary"
                    type="button"
                    @click="showPassword = !showPassword"
                  >
                    <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                  </button>
                </div>
                <div class="invalid-feedback d-block" v-if="errors.password">
                  {{ errors.password }}
                </div>
                <small class="form-text text-muted">
                  Mínimo 8 caracteres
                </small>
              </div>

              <div class="col-md-6">
                <label for="confirmPassword" class="form-label">
                  Confirmar Contraseña <span class="text-danger">*</span>
                </label>
                <div class="input-group">
                  <input
                    :type="showConfirmPassword ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': errors.confirmPassword }"
                    id="confirmPassword"
                    v-model="confirmPassword"
                    placeholder="••••••••"
                    required
                    minlength="8"
                  >
                  <button
                    class="btn btn-outline-secondary"
                    type="button"
                    @click="showConfirmPassword = !showConfirmPassword"
                  >
                    <i :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                  </button>
                </div>
                <div class="invalid-feedback d-block" v-if="errors.confirmPassword">
                  {{ errors.confirmPassword }}
                </div>
              </div>
            </div>
          </div>

          <!-- Role and Client Assignment -->
          <div class="form-section mb-4">
            <h5 class="form-section-title">
              <i class="bi bi-diagram-3 me-2"></i>
              Asignaciones
            </h5>

            <div class="row g-3">
              <div class="col-md-6">
                <label for="rol_id" class="form-label">
                  Rol
                </label>
                <select
                  class="form-select"
                  :class="{ 'is-invalid': errors.rol_id }"
                  id="rol_id"
                  v-model="formData.rol_id"
                >
                  <option :value="null">Sin rol asignado</option>
                  <option v-for="role in availableRoles" :key="role.id" :value="role.id">
                    {{ role.name }}
                  </option>
                </select>
                <div class="invalid-feedback" v-if="errors.rol_id">
                  {{ errors.rol_id }}
                </div>
                <small class="form-text text-muted">
                  Rol que define los permisos del usuario
                </small>
              </div>

              <div class="col-md-6">
                <label for="client_id" class="form-label">
                  Cliente
                </label>
                <select
                  class="form-select"
                  :class="{ 'is-invalid': errors.client_id }"
                  id="client_id"
                  v-model="formData.client_id"
                >
                  <option :value="null">Sin cliente asignado</option>
                  <option v-for="client in availableClients" :key="client.id" :value="client.id">
                    {{ client.nombre }} ({{ client.cuit }})
                  </option>
                </select>
                <div class="invalid-feedback" v-if="errors.client_id">
                  {{ errors.client_id }}
                </div>
                <small class="form-text text-muted">
                  Cliente asociado al usuario (opcional)
                </small>
              </div>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="form-actions">
            <router-link to="/users" class="btn btn-secondary">
              <i class="bi bi-x-lg me-2"></i>
              Cancelar
            </router-link>
            <button type="submit" class="btn btn-primary" :disabled="userStore.loading">
              <span v-if="userStore.loading">
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Creando...
              </span>
              <span v-else>
                <i class="bi bi-check-lg me-2"></i>
                Crear Usuario
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '../../stores/users'
import { useRoleStore } from '../../stores/roles'
import { useClientStore } from '../../stores/clients'

// Router and Stores
const router = useRouter()
const userStore = useUserStore()
const roleStore = useRoleStore()
const clientStore = useClientStore()

// Form data
const formData = reactive({
  fullname: '',
  email: '',
  password: '',
  rol_id: null,
  client_id: null
})

const confirmPassword = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const errors = ref({})

// Available options
const availableRoles = ref([])
const availableClients = ref([])

// Methods
const validateForm = () => {
  errors.value = {}

  // Validate email
  if (!formData.email) {
    errors.value.email = 'El email es requerido'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.value.email = 'El email no es válido'
  }

  // Validate password
  if (!formData.password) {
    errors.value.password = 'La contraseña es requerida'
  } else if (formData.password.length < 8) {
    errors.value.password = 'La contraseña debe tener al menos 8 caracteres'
  }

  // Validate confirm password
  if (!confirmPassword.value) {
    errors.value.confirmPassword = 'Debe confirmar la contraseña'
  } else if (formData.password !== confirmPassword.value) {
    errors.value.confirmPassword = 'Las contraseñas no coinciden'
  }

  return Object.keys(errors.value).length === 0
}

const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  try {
    const userData = {
      fullname: formData.fullname || undefined,
      email: formData.email,
      password: formData.password,
      rol_id: formData.rol_id || undefined,
      client_id: formData.client_id || undefined
    }

    await userStore.createUser(userData)
    router.push('/users')
  } catch (error) {
    console.error('Error creating user:', error)
    // Errors are handled by the store
  }
}

// Lifecycle
onMounted(async () => {
  try {
    // Load roles (active only)
    await roleStore.fetchRoles(1, 100)
    availableRoles.value = roleStore.roles.filter(role => !role.is_deleted)

    // Load clients (active only)
    await clientStore.fetchClients({ page: 1, size: 100 })
    availableClients.value = clientStore.clients.filter(client => !client.deleted_at)
  } catch (error) {
    console.error('Error loading form data:', error)
  }
})
</script>

<style scoped>
.user-create {
  padding: 0;
}

.page-header {
  background: var(--card-bg);
  border-bottom: 1px solid var(--border-color);
  padding: 1.5rem 0;
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.title-section h1 {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-primary);
}

.title-section p {
  margin: 0.25rem 0 0;
  color: var(--text-secondary);
}

.card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.form-section {
  padding: 1.5rem;
  background: var(--section-bg);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.form-section-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--text-primary);
  display: flex;
  align-items: center;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
  margin-top: 1.5rem;
}

.form-label {
  font-weight: 500;
  color: var(--text-primary);
}

.form-text {
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .form-actions {
    flex-direction: column-reverse;
  }

  .form-actions .btn {
    width: 100%;
  }
}
</style>
